import { ActionsDispatcher } from 'store/ActionsDispatcher';

import {
    StreamRequestInfo,
    WidgetCustomManagedStreamOptions,
} from './WidgetCustomStreamManager';
import {
    selectAllStoppedStreamStatuses,
    selectChannelId,
} from 'streamsState/store/streamStatuses/selectors';
import {
    createClearStreamDataAction,
    createRemoveStreamDataAction,
} from 'streamsState/store/streamedData/actions';
import { WidgetStreamByRequestUidStreamManagerBase } from 'widgets/streaming/widgetStreamManager/WidgetStreamByRequestUidStreamManagerBase';
import StreamDataService, {
    StreamDataHandlers,
} from 'streamsState/StreamDataService';
import orderBy from 'lodash/orderBy';
import { createStreamStatusRemoveAction } from 'streamsState/store/streamStatuses/actions';
import { ChannelDestination } from 'communication/Channel';
import { buildByStreamKeyHandlers } from 'streamsState/store/streamedData/handlers';
import { Store } from 'redux';
import { LoggingService } from '@instinet-analytics/core-lib';

export interface WidgetStreamByRequestUidStreamManagerOptions<TMessage>
    extends WidgetCustomManagedStreamOptions {
    clearOnNewRequest?: (requestInfo: StreamRequestInfo<unknown>) => boolean;
    checkOrderIds?: boolean;
    buildHandlers?: (
        store: Store,
        loggingService: LoggingService
    ) => StreamDataHandlers<TMessage>;
}

const RetainStaleStreamsQty = 10;

export class WidgetStreamByRequestUidStreamManager<
    TMessage = unknown,
> extends WidgetStreamByRequestUidStreamManagerBase {
    protected readonly streamDataService: StreamDataService;
    private readonly channelDestination: ChannelDestination;

    private buildHandlers?: (
        store: Store,
        loggingService: LoggingService
    ) => StreamDataHandlers<TMessage>;
    private messageHandlers: {
        [streamKey: string]: StreamDataHandlers<TMessage>;
    } = {};
    private loggingService: LoggingService;
    private clearOnNewRequest?: (
        requestInfo: StreamRequestInfo<unknown>
    ) => boolean;

    public constructor(
        options: WidgetStreamByRequestUidStreamManagerOptions<TMessage>
    ) {
        super(
            'WidgetStreamByRequestUidStreamManager-' +
                options.channelDestination,
            options.channelDestination,
            options
        );
        this.streamDataService = options.streamDataService;
        this.channelDestination = options.channelDestination;
        this.buildHandlers = options.buildHandlers;
        this.loggingService = options.loggingService;
        this.clearOnNewRequest = options.clearOnNewRequest;
    }

    public resetState() {
        super.resetState();
    }

    protected async startStream(
        requestInfo: StreamRequestInfo<unknown>,
        dispatcher: ActionsDispatcher,
        reason: string
    ) {
        const { streamKey, payload } = requestInfo;

        const currentChannelId = selectChannelId(
            this.store.getState(),
            streamKey
        );

        this.log('Starting stream', reason, { streamKey, payload });

        // Unless a callback is provided to check, we don't clear the stream as the streams are unique per request
        if (this.clearOnNewRequest && this.clearOnNewRequest(requestInfo)) {
            dispatcher.dispatch(
                createClearStreamDataAction(this.channelDestination, streamKey)
            );
        }

        await this.streamDataService.startAndWatchStream(
            streamKey,
            this.channelDestination,
            this.getMessageHandler(streamKey),
            currentChannelId,
            dispatcher
        )(payload);
    }

    protected stopStream(
        streamKey: string,
        dispatcher: ActionsDispatcher,
        reason: string
    ) {
        this.streamDataService.stopStream(streamKey, dispatcher, false);
        this.log('Stopping stream', reason, streamKey);
    }

    /*
     * When streams are stopped we remove stale data (keeping a couple in case the user clicks back)
     */
    protected clearAndRemoveStaleStreamData(
        dispatcher: ActionsDispatcher,
        adding: string[],
        removing: string[],
        _next: string[]
    ) {
        if (removing.length === 0) {
            return [];
        }

        const state = this.store.getState();
        const stoppedStreams = selectAllStoppedStreamStatuses(state);
        const myStoppedStreams = stoppedStreams
            .filter((s) => this.streamKeys.includes(s.streamKey))
            .filter((s) => !adding.includes(s.streamKey));

        if (myStoppedStreams.length === 0) {
            return [];
        }

        // Keep all the streams currently being removed, but purge older ones...
        const totalStoppedStreamCount =
            removing.length + myStoppedStreams.length;
        const totalStaleCount = totalStoppedStreamCount - RetainStaleStreamsQty;
        const oldStaleCount = Math.min(
            totalStaleCount,
            myStoppedStreams.length
        );

        if (oldStaleCount <= 0) {
            return [];
        }

        const streamsToKill = orderBy(
            myStoppedStreams,
            ['stoppedTime'],
            ['asc']
        ).slice(0, oldStaleCount);

        for (const staleStreamKey of streamsToKill) {
            this.streamKeys = this.streamKeys.filter(
                (k) => k !== staleStreamKey.streamKey
            );

            dispatcher.dispatch(
                createRemoveStreamDataAction(
                    this.channelDestination,
                    staleStreamKey.streamKey
                )
            );
            dispatcher.dispatch(
                createStreamStatusRemoveAction(staleStreamKey.streamKey)
            );
        }

        return streamsToKill.map((s) => s.streamKey);
    }

    public getInfo() {
        return {
            ...super.getInfo(),
            channelDestination: this.channelDestination,
        };
    }

    private getMessageHandler(streamKey: string): StreamDataHandlers<TMessage> {
        // We use handler per stream key i.e. so we buffer per stream...
        if (!this.messageHandlers[streamKey]) {
            this.messageHandlers[streamKey] = this.buildHandlers
                ? this.buildHandlers(this.store, this.loggingService)
                : buildByStreamKeyHandlers(this.store, this.channelDestination);
        }
        return this.messageHandlers[streamKey];
    }
}
